import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import ScenarioTitlePageTags from './ScenarioTitlePageTags';
import styles from './styles/ScenarioTitlePage.module.css';
import TextEditor from '../../texteditor/TextEditor';
import 'ckeditor5/ckeditor5.css';

const ScenarioTitlePage = () => {
    const navigate = useNavigate();
    const { scenarioId } = useParams();
    const { userId } = useContext(AuthContext);

    const [scenario, setScenario] = useState({ title: '', description: '', image: null });
    const [tags, setTags] = useState([]);
    const [isPrivate, setIsPrivate] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [password, setPassword] = useState('');
    const [isHidden, setIsHidden] = useState(false);
    const [isLogged, setIsLogged] = useState(false);
    const [initialData, setInitialData] = useState('');
    const [originalIsLogged, setOriginalIsLogged] = useState(false);
    const editorRef = useRef(null);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: useCallback((acceptedFiles) => {
            if (acceptedFiles.length) {
                setScenario((prev) => ({ ...prev, image: acceptedFiles[0] }));
            }
        }, []),
    });

    const fetchScenarioData = useCallback(async () => {
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenarioId}`, {
                headers: { Authorization: `Bearer ${userId}` },
            });

            if (data) {
                setScenario({
                    title: data.title || '',
                    description: data.description || '',
                    image: data.image || null,
                });
                setIsPrivate(data.isPrivate || false);
                setPassword(data.password || '');
                setIsHidden(data.isHidden || false);
                setIsLogged(data.isLogged || false);
                setShowMap(data.showMap || false);
                setTags(data.tags || []);
                setInitialData(data.description);

                setOriginalIsLogged(data.isLogged || false);

                if (!data.isEditing) {
                    await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenarioId}`, { isEditing: true });
                }
            }
        } catch (error) {
            console.error('Error fetching scenario data:', error);
            if (error.response?.status === 403) {
                navigate('/home');
            } else {
                alert('An error occurred while verifying ownership.');
            }
        }
    }, [scenarioId, userId, navigate]);

    useEffect(() => {
        fetchScenarioData();
    }, [fetchScenarioData]);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.setData(initialData);
        }
    }, [initialData]);

    const handleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        setScenario((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleCheckboxChange = useCallback(() => {
        setIsPrivate((prev) => {
            if (prev) setPassword('');
            return !prev;
        });
    }, []);

    const handleHiddenChange = useCallback(() => {
        setIsHidden((prev) => !prev);
    }, []);

    const handleLoggedChange = useCallback(() => {
        setIsLogged((prev) => !prev);
    }, []);

    const handleShowMapChange = useCallback(() => {
        setShowMap((prev) => !prev);
    }, []);

    const handleSubmit = useCallback(async () => {
        try {
            const formData = new FormData();
            formData.append('title', scenario.title);
            formData.append('description', scenario.description);
            formData.append('tags', tags.join(','));
            formData.append('isPrivate', isPrivate);
            formData.append('password', password);
            formData.append('isHidden', isHidden);
            formData.append('isLogged', isLogged);
            formData.append('showMap', showMap);
            if (scenario.image) formData.append('image', scenario.image);

            await axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenarioId}`, formData);
            console.log('Scenario updated successfully');

            if (isLogged !== originalIsLogged) {
                const { data: situations } = await axios.get(`${process.env.REACT_APP_API_URL_LOCAL}/api/situations/${scenarioId}/all`, {
                    headers: { Authorization: `Bearer ${userId}` },
                });

                await Promise.all(
                    situations.map((situation) =>
                        axios.patch(`${process.env.REACT_APP_API_URL_LOCAL}/api/situations/situation/${situation._id}`, { isLogged })
                    )
                );
            }

            navigate(`/create/${scenarioId}/canvas`);
        } catch (error) {
            console.error('Error updating scenario:', error);
        }
    }, [scenario, tags, isPrivate, password, isHidden, isLogged, showMap, scenarioId, userId, originalIsLogged, navigate]);

    const deleteImageFromServer = useCallback(async () => {
        try {
            await axios.patch(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenarioId}`,
                { image: null }
            );
            setScenario((prev) => ({ ...prev, image: null }));
            console.log('Image deleted successfully');
        } catch (error) {
            console.error('Error deleting image:', error);
        }
    }, [scenarioId]);

    const handleTextEditorChange = (data) => {
        setScenario((prev) => ({
            ...prev,
            description: data
        }));
    };

    

    return (
        <div className={styles.pageContainer}>
            <h1 className={styles.pageTitle}>Edit Your Title Page</h1>
            <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
                <div className={styles.formField}>
                    <div
                        id="scenarioImageUpload"
                        {...getRootProps()}
                        className={`${styles.imageDropzone} ${scenario.image ? styles.hasImage : ''}`}
                        tabIndex={0}
                    >
                        <input {...getInputProps()} />
                        {scenario.image ? (
                            <>
                                <div className={styles.imagePreviewContainer}>
                                    <img
                                        className={styles.imagePreview}
                                        src={
                                            typeof scenario.image === 'string'
                                                ? scenario.image
                                                : URL.createObjectURL(scenario.image)
                                        }
                                        alt="Scenario"
                                    />
                                </div>
                                <div className={styles.imageOverlay}>
                                    <span className={styles.imageOverlayText}>Click to change image</span>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteImageFromServer();
                                        }}
                                        className={`${styles.buttonIcon} ${styles.removeImageButton}`}
                                        aria-label="Remove image"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className={styles.dropzonePlaceholder}>
                                <svg xmlns="http://www.w3.org/2000/svg" className={styles.uploadIcon} viewBox="0 0 24 24" fill="currentColor"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/></svg>
                                <p className={styles.dropzoneText}>Drag & drop image or click to upload</p>
                                <p className={styles.dropzoneHint}>(Max 5MB)</p>
                            </div>
                        )}
                    </div>
                </div>
    
                <div className={styles.formField}>
                    <label className={styles.label} htmlFor="scenarioTitle">Title</label>
                    <input
                        id="scenarioTitle"
                        type="text"
                        name="title"
                        autoFocus
                        value={scenario.title}
                        onChange={handleInputChange}
                        className={styles.inputField}
                        maxLength={60}
                        placeholder="Enter scenario title"
                    />
                    <div className={styles.charCount}>{60 - scenario.title.length} characters remaining</div>
                </div>
    
                <div className={styles.formField}>
                    <label className={styles.label} htmlFor="scenarioDescriptionEditor">Description</label>
                    <div className={styles.textEditorWrapper} id="scenarioDescriptionEditor">
                        <TextEditor
                            initialData={initialData}
                            onChange={handleTextEditorChange}
                        />
                    </div>
                </div>
    
                <div className={styles.formField}>
                    <h2 className={styles.subheading}>Settings</h2>
                    <div className={styles.checkboxItem}>
                        <label className={styles.checkboxLabel} htmlFor="isPrivateCheckbox">
                            <input
                                id="isPrivateCheckbox"
                                type="checkbox"
                                checked={isPrivate}
                                onChange={handleCheckboxChange}
                                className={styles.checkboxInput}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Add a Password</span>
                        </label>
                        {isPrivate && (
                            <div className={styles.passwordFieldContainer}>
                                <label className={styles.labelNested} htmlFor="scenarioPassword">Enter a password</label>
                                <input
                                    id="scenarioPassword"
                                    type="text"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={styles.inputField}
                                    placeholder="Enter password"
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.checkboxItem}>
                        <label className={styles.checkboxLabel} htmlFor="isHiddenCheckbox">
                            <input
                                id="isHiddenCheckbox"
                                type="checkbox"
                                checked={isHidden}
                                onChange={handleHiddenChange}
                                className={styles.checkboxInput}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Hide From Public</span>
                        </label>
                    </div>
                    <div className={styles.checkboxItem}>
                        <label className={styles.checkboxLabel} htmlFor="isLoggedCheckbox">
                            <input
                                id="isLoggedCheckbox"
                                type="checkbox"
                                checked={isLogged}
                                onChange={handleLoggedChange}
                                className={styles.checkboxInput}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Create Logs</span>
                        </label>
                    </div>
                     <div className={styles.checkboxItem}>
                        <label className={styles.checkboxLabel} htmlFor="showMapCheckbox">
                            <input
                                id="showMapCheckbox"
                                type="checkbox"
                                checked={showMap}
                                onChange={handleShowMapChange}
                                className={styles.checkboxInput}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>Display Map</span>
                        </label>
                    </div>
                </div>
    
                <div className={styles.formField}>
                    <label className={styles.label} htmlFor="scenarioTags">Tags</label>
                    <div id="scenarioTags">
                        <ScenarioTitlePageTags tags={tags} setTags={setTags} />
                    </div>
                </div>
    
                <div className={styles.formActions}>
                    <button type="button" className={`${styles.button} ${styles.secondaryButton}`} onClick={() => navigate('/home')}>
                        Home
                    </button>
                    <button type="button" className={`${styles.button} ${styles.primaryButton}`} onClick={handleSubmit}>
                        Next
                    </button>
                </div>
            </form>
        </div>
    );
    
};

export default React.memo(ScenarioTitlePage);
